import { CheckCircleOutline } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useImportMapitShipOrdersXlsMutation } from "src/_api/mapitShipApi";
import { useGetWarehousesQuery } from "src/_api/warehouseApi";
import { InputExcelFile, IssuesListDialog } from "src/_modules/common";
import { $BASE_URL } from "src/constants";

export default function ImportMapitShipOrdersDialog({ open, onClose }) {
  const { t } = useTranslation();
  const [fileUploadLoading, setFileUploadLoading] = useState(false);

  const [
    importMapitShipOrdersXls,
    { data, isLoading, isSuccess, error, reset },
  ] = useImportMapitShipOrdersXlsMutation();
  
  const { data: warehouses, isLoading: warehousesLoading } =
    useGetWarehousesQuery({ limit: 100 });

  const [base64, setBase64] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  const handleChange = ({ target: { value } }) => {
    setBase64(value);
    if (isSuccess) reset();
  };

  const closeDialogHandler = () => {
    onClose();
    setBase64(null);
    setSelectedWarehouse(null);
  };

  const disabledConfirm = useMemo(() => {
    if (isSuccess || !base64 || !selectedWarehouse) return true;
    return false;
  }, [isSuccess, base64, selectedWarehouse]);

  const [openIssuesListDialog, setOpenIssuesListDialog] = useState(false);
  const [ordersIssues, setOrdersIssues] = useState([]);

  const closeIssuesListDialogHandler = () => {
    setOpenIssuesListDialog(false);
    setOrdersIssues([]);
  };

  const confirmHandler = async () => {
    try {
      let response = await importMapitShipOrdersXls({
        base64,
        warehouse: selectedWarehouse?.id,
      }).unwrap();
      if (response?.errors?.length) {
        setOrdersIssues(response?.errors);
        setOpenIssuesListDialog(true);
      } else {
        setTimeout(() => {
          closeDialogHandler();
        }, 2000);
      }
    } catch (error) {
      console.log("error");
      console.log(error);
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <>
      <IssuesListDialog
        open={openIssuesListDialog}
        errorLable={"Order No."}
        description={
          <Typography
            variant="subtitle1"
            sx={{ color: "text.lighter", letterSpacing: "normal" }}
          >
            <span style={{ color: "red" }}>
              {ordersIssues.length > 1
                ? t(`IssuesCount`, { count: ordersIssues.length })
                : t("Issue")}
            </span>
            {t("OrdersIssuesMsg", { status: t("upload") })}
          </Typography>
        }
        issues={ordersIssues}
        onClose={closeIssuesListDialogHandler}
      />
      <Dialog open={open} onClose={(e) => e.preventDefault()} fullWidth>
        <DialogTitle>{t("importOrdersDialog.title")}</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="subtitle1">
            {t("importOrdersDialog.message")}
          </Typography>
          <Box
            sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}
          >
            <Typography variant="subtitle1">
              {t("importOrdersDialog.excelSchemaPropmt")}
            </Typography>
            <Button
              disabled={false}
              color="main"
              sx={{ textTransform: "none" }}
              href={`${$BASE_URL}/mapit_ship_orders.xlsx`}
            >
              {t("importOrdersDialog.clickHere")}
            </Button>
          </Box>
          <Autocomplete
            fullWidth
            getOptionLabel={(option) =>
              option && option.name ? option.name : ""
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => setSelectedWarehouse(newValue)}
            disableClearable
            value={selectedWarehouse}
            size="small"
            loading={warehousesLoading}
            loadingText={t("loading data...")}
            options={warehouses?.docs ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(`Select warehouse`)}
                error={Boolean(error)}
                helperText={error}
                required
              />
            )}
          />
          <Box
            sx={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <LoadingButton
              loading={fileUploadLoading}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                marginInlineEnd: "16px",
                my: 1.5,
              }}
              variant="contained"
              component="label"
            >
              {t("importOrdersDialog.choose") + " *"}
              <InputExcelFile
                // order="task-orders-excel-file"
                name="base64"
                onUploadStart={() => {
                  setFileUploadLoading(true);
                }}
                onUploadEnded={() => {
                  setFileUploadLoading(false);
                }}
                handleChange={handleChange}
              />
            </LoadingButton>
            {!!base64 && (
              <Box display="flex" alignItems="center" mt={2} pb={2}>
                <CheckCircleOutline color="success" sx={{ mr: 2 }} />
                <Typography color="success.main">
                  {t("File is ready")}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          {!!error && <Typography color="error">{t(error)}</Typography>}
          {isSuccess && !data?.errors && (
            <Typography color="green.main">{t("Done successfully")}</Typography>
          )}
          <Box mr="auto" />
          <LoadingButton
            variant="contained"
            onClick={closeDialogHandler}
            sx={{ textTransform: "none" }}
            disabled={isLoading}
            loading={isLoading}
          >
            {t("importOrdersDialog.cancel")}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            sx={{ textTransform: "none", marginInlineEnd: "16px", mx: 1 }}
            onClick={confirmHandler}
            disabled={disabledConfirm}
            loading={isLoading}
            disableElevation
          >
            {t("importOrdersDialog.confirm")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
